import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Edit2, Trash2, Phone, Mail, Globe, Upload, Search, ToggleLeft, ToggleRight, Save, X, ShoppingCart, Store, Link2, ChevronLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIT_NAMES = {
  "1": "Kit 2 L", "2": "Kit 4 L", "3": "Kit 12 L", "4": "Kit 25 L",
  "5": "PRO Lite 650", "6": "PRO 970", "7": "PRO 1.400", "8": "Dual 3.900", "9": "Dual 7.200"
};

const AdminDealers = () => {
  const [tab, setTab] = useState('physical');
  const [dealers, setDealers] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [uploadStatus, setUploadStatus] = useState(null);
  const [geoStatus, setGeoStatus] = useState(null);
  const fileInputRef = useRef(null);

  // Dealer form
  const [dealerForm, setDealerForm] = useState({ name: '', address: '', city: '', postcode: '', provincia: '', lat: '', lng: '', phone: '', email: '', website: '', active: true });
  // Shop form
  const [shopForm, setShopForm] = useState({ name: '', logo_url: '', position: 1, product_links: {}, active: true });

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    try {
      const [dRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dealers`, { headers }),
        axios.get(`${API_URL}/api/admin/online-shops`, { headers })
      ]);
      setDealers(dRes.data);
      setShops(sRes.data);
    } catch {
      setUploadStatus('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const fetchGeoStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/dealers/geocoding-status`, { headers });
      setGeoStatus(res.data);
    } catch {
      // Geocoding-status badge is optional — silently skip if unavailable.
    }
  };

  useEffect(() => { fetchAll(); fetchGeoStatus(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll geocoding status every 5s while there are pending dealers
  useEffect(() => {
    if (!geoStatus || geoStatus.status_pending === 0) return;
    const id = setInterval(() => { fetchGeoStatus(); fetchAll(); }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoStatus?.status_pending]);

  const filteredDealers = useMemo(() => {
    return dealers.filter(d => {
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || d.name?.toLowerCase().includes(q) || d.address?.toLowerCase().includes(q) || d.city?.toLowerCase().includes(q) || d.postcode?.toLowerCase().includes(q) || (d.provincia || d.regions?.[0] || '').toLowerCase().includes(q);
      const matchActive = filterActive === 'all' || (filterActive === 'active' && d.active) || (filterActive === 'inactive' && !d.active);
      return matchSearch && matchActive;
    });
  }, [dealers, searchQuery, filterActive]);

  const filteredShops = useMemo(() => {
    return shops.filter(s => {
      const matchSearch = !searchQuery || s.name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchActive = filterActive === 'all' || (filterActive === 'active' && s.active) || (filterActive === 'inactive' && !s.active);
      return matchSearch && matchActive;
    });
  }, [shops, searchQuery, filterActive]);

  const resetForm = () => { setShowForm(false); setEditingItem(null); setDealerForm({ name: '', address: '', city: '', postcode: '', provincia: '', lat: '', lng: '', phone: '', email: '', website: '', active: true }); setShopForm({ name: '', logo_url: '', position: 1, product_links: {}, active: true }); };

  // ======== DEALER CRUD ========
  const handleEditDealer = (d) => {
    setDealerForm({
      name: d.name || '',
      address: d.address || '',
      city: d.city || '',
      postcode: d.postcode || '',
      provincia: d.provincia || d.regions?.[0] || '',
      lat: d.lat ?? '',
      lng: d.lng ?? '',
      phone: d.phone || '',
      email: d.email || '',
      website: d.website || '',
      active: d.active !== false,
    });
    setEditingItem(d); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleSaveDealer = async () => {
    const prov = (dealerForm.provincia || '').trim();
    const payload = {
      ...dealerForm,
      provincia: prov || null,
      regions: prov ? [prov] : [],
      lat: dealerForm.lat !== '' ? parseFloat(dealerForm.lat) : null,
      lng: dealerForm.lng !== '' ? parseFloat(dealerForm.lng) : null,
    };
    try {
      if (editingItem) { await axios.put(`${API_URL}/api/admin/dealers/${editingItem.dealer_id}`, payload, { headers }); }
      else { await axios.post(`${API_URL}/api/admin/dealers`, payload, { headers }); }
      fetchAll(); resetForm();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore nel salvataggio';
      alert(msg);
    }
  };
  const handleDeleteDealer = async (id) => { if (!window.confirm('Eliminare?')) return; try { await axios.delete(`${API_URL}/api/admin/dealers/${id}`, { headers }); fetchAll(); } catch { alert('Eliminazione fallita'); } };
  const handleToggleDealer = async (d) => { try { await axios.put(`${API_URL}/api/admin/dealers/${d.dealer_id}/toggle`, {}, { headers }); fetchAll(); } catch { alert('Cambio stato fallito'); } };

  // ======== SHOP CRUD ========
  const handleEditShop = (s) => {
    setShopForm({ name: s.name || '', logo_url: s.logo_url || '', position: s.position || 1, product_links: s.product_links || {}, active: s.active !== false });
    setEditingItem(s); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleSaveShop = async () => {
    try {
      if (editingItem?.shop_id) { await axios.put(`${API_URL}/api/admin/online-shops/${editingItem.shop_id}`, shopForm, { headers }); }
      else { await axios.post(`${API_URL}/api/admin/online-shops`, shopForm, { headers }); }
      fetchAll(); resetForm();
    } catch { alert('Errore nel salvataggio'); }
  };
  const handleDeleteShop = async (id) => { if (!window.confirm('Eliminare?')) return; try { await axios.delete(`${API_URL}/api/admin/online-shops/${id}`, { headers }); fetchAll(); } catch { alert('Eliminazione fallita'); } };
  const handleToggleShop = async (s) => {
    try { await axios.put(`${API_URL}/api/admin/online-shops/${s.shop_id}`, { active: !s.active }, { headers }); fetchAll(); } catch { alert('Cambio stato fallito'); }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadStatus('uploading');
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/admin/dealers/upload-excel`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setUploadStatus(`Importati: ${res.data.imported}, Aggiornati: ${res.data.updated}`);
      fetchAll();
    } catch { setUploadStatus('Errore nel caricamento'); }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleExcelReplace = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (!window.confirm('ATTENZIONE: Tutti i rivenditori attuali verranno eliminati e sostituiti con i dati del file. Continuare?')) { if (fileInputRef.current) fileInputRef.current.value = ''; return; }
    setUploadStatus('uploading');
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/admin/dealers/upload-replace`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      const geoNote = res.data.to_geocode > 0 ? ` — Geocoding in corso per ${res.data.to_geocode} rivenditori (~${Math.ceil(res.data.to_geocode * 1.1)}s)` : '';
      setUploadStatus(`Eliminati: ${res.data.deleted}, Importati: ${res.data.imported}${geoNote}`);
      fetchAll(); fetchGeoStatus();
    } catch { setUploadStatus('Errore nel caricamento'); }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGeocodeMissing = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/dealers/geocode-missing`, {}, { headers });
      if (res.data.queued > 0) {
        setUploadStatus(`Geocoding avviato per ${res.data.queued} rivenditori (~${res.data.estimated_seconds}s)`);
        fetchGeoStatus();
      } else {
        setUploadStatus('Tutti i rivenditori hanno già le coordinate');
      }
    } catch { setUploadStatus('Errore nel geocoding'); }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Eliminare TUTTI i rivenditori fisici? Questa azione non è reversibile.')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/admin/dealers/bulk`, { headers, data: { delete_all: true } });
      setUploadStatus(res.data.message);
      fetchAll();
    } catch { setUploadStatus('Errore nell\'eliminazione'); }
  };

  const updateShopLink = (kitId, url) => {
    setShopForm(prev => ({ ...prev, product_links: { ...prev.product_links, [kitId]: url } }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <a href="/calcola-preventivo/admin6y5dv2x/dashboard" className="text-gray-500 hover:text-gray-700"><ChevronLeft className="w-5 h-5" /></a>
            <h1 className="text-xl font-bold text-gray-900">Rivenditori</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            {tab === 'physical' && (
              <>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} className="hidden" />
                <Button data-testid="upload-excel-btn" variant="outline" onClick={() => fileInputRef.current?.click()} className="text-xs"><Upload className="w-3.5 h-3.5 mr-1" /> Aggiungi Excel</Button>
                <Button data-testid="upload-replace-btn" variant="outline" onClick={() => { fileInputRef.current.onchange = handleExcelReplace; fileInputRef.current?.click(); }} className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"><Upload className="w-3.5 h-3.5 mr-1" /> Sostituisci tutti</Button>
                <Button data-testid="bulk-delete-all-btn" variant="outline" onClick={handleDeleteAll} className="text-xs border-red-300 text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina tutti</Button>
              </>
            )}
            <Button data-testid="add-item-btn" onClick={() => { resetForm(); setShowForm(true); }} className="bg-stocker-green hover:bg-stocker-green-dark text-white text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" /> Nuovo
            </Button>
          </div>
        </div>

        {uploadStatus && (
          <div className={`mb-3 p-2.5 rounded-lg text-xs ${uploadStatus === 'uploading' ? 'bg-blue-50 text-blue-700' : uploadStatus.startsWith('Errore') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {uploadStatus === 'uploading' ? 'Caricamento...' : uploadStatus}
            <button onClick={() => setUploadStatus(null)} className="ml-2 underline">Chiudi</button>
          </div>
        )}

        {/* Geocoding status bar */}
        {tab === 'physical' && geoStatus && (geoStatus.without_coords > 0 || geoStatus.status_pending > 0) && (
          <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs flex flex-col sm:flex-row items-start sm:items-center gap-2 justify-between">
            <div className="text-amber-800">
              {geoStatus.status_pending > 0 ? (
                <><span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse mr-1"></span><strong>Geocoding in corso:</strong> {geoStatus.status_ok} su {geoStatus.total} completati</>
              ) : (
                <><strong>⚠ Coordinate mancanti:</strong> {geoStatus.without_coords} rivenditori non hanno latitudine/longitudine e non saranno trovati nel routing.</>
              )}
            </div>
            {geoStatus.status_pending === 0 && geoStatus.without_coords > 0 && (
              <Button data-testid="geocode-missing-btn" variant="outline" size="sm" onClick={handleGeocodeMissing} className="text-xs border-amber-400 text-amber-700 hover:bg-amber-100">
                Geocodifica ora ({geoStatus.without_coords})
              </Button>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1">
          <button onClick={() => { setTab('physical'); resetForm(); setSearchQuery(''); }} className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'physical' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <Store className="w-4 h-4" /> Fisici <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">{dealers.length}</span>
          </button>
          <button onClick={() => { setTab('online'); resetForm(); setSearchQuery(''); }} className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'online' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <ShoppingCart className="w-4 h-4" /> Online Shop <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">{shops.length}</span>
          </button>
        </div>

        {/* ======== DEALER FORM ======== */}
        {showForm && tab === 'physical' && (
          <div data-testid="dealer-form" className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
            <h2 className="font-bold text-sm mb-3">{editingItem ? 'Modifica Rivenditore' : 'Nuovo Rivenditore'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div><Label className="text-xs">Nome *</Label><Input data-testid="dealer-name" value={dealerForm.name} onChange={e => setDealerForm(p => ({ ...p, name: e.target.value }))} placeholder="Nome" className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Indirizzo</Label><Input value={dealerForm.address} onChange={e => setDealerForm(p => ({ ...p, address: e.target.value }))} placeholder="Via Roma 1" className="mt-1 h-9" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">CAP</Label><Input data-testid="dealer-postcode" value={dealerForm.postcode} onChange={e => setDealerForm(p => ({ ...p, postcode: e.target.value }))} placeholder="56100" className="mt-1 h-9" /></div>
                <div><Label className="text-xs">Provincia</Label><Input data-testid="dealer-provincia" value={dealerForm.provincia} onChange={e => setDealerForm(p => ({ ...p, provincia: e.target.value }))} placeholder="PI" maxLength={20} className="mt-1 h-9" /></div>
              </div>
              <div><Label className="text-xs">Città</Label><Input data-testid="dealer-city" value={dealerForm.city} onChange={e => setDealerForm(p => ({ ...p, city: e.target.value }))} placeholder="Pisa" className="mt-1 h-9" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Lat</Label><Input type="number" step="any" value={dealerForm.lat} onChange={e => setDealerForm(p => ({ ...p, lat: e.target.value }))} className="mt-1 h-9" /></div>
                <div><Label className="text-xs">Lng</Label><Input type="number" step="any" value={dealerForm.lng} onChange={e => setDealerForm(p => ({ ...p, lng: e.target.value }))} className="mt-1 h-9" /></div>
              </div>
              <div><Label className="text-xs">Telefono</Label><Input value={dealerForm.phone} onChange={e => setDealerForm(p => ({ ...p, phone: e.target.value }))} className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Email</Label><Input value={dealerForm.email} onChange={e => setDealerForm(p => ({ ...p, email: e.target.value }))} className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Website</Label><Input value={dealerForm.website} onChange={e => setDealerForm(p => ({ ...p, website: e.target.value }))} className="mt-1 h-9" /></div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={dealerForm.active} onChange={e => setDealerForm(p => ({ ...p, active: e.target.checked }))} className="rounded" /> Attivo</label>
              <div className="flex-1" />
              <Button variant="outline" size="sm" onClick={resetForm}><X className="w-3 h-3 mr-1" /> Annulla</Button>
              <Button data-testid="save-dealer-btn" size="sm" onClick={handleSaveDealer} className="bg-stocker-green text-white"><Save className="w-3 h-3 mr-1" /> Salva</Button>
            </div>
          </div>
        )}

        {/* ======== SHOP FORM ======== */}
        {showForm && tab === 'online' && (
          <div data-testid="shop-form" className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
            <h2 className="font-bold text-sm mb-3">{editingItem?.shop_id ? 'Modifica Online Shop' : 'Nuovo Online Shop'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div><Label className="text-xs">Nome *</Label><Input value={shopForm.name} onChange={e => setShopForm(p => ({ ...p, name: e.target.value }))} placeholder="Es. Amazon" className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Logo URL</Label><Input value={shopForm.logo_url} onChange={e => setShopForm(p => ({ ...p, logo_url: e.target.value }))} placeholder="https://..." className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Posizione</Label><Input type="number" value={shopForm.position} onChange={e => setShopForm(p => ({ ...p, position: parseInt(e.target.value) || 1 }))} className="mt-1 h-9" /></div>
            </div>
            <div className="mb-3">
              <Label className="text-xs font-semibold mb-2 block">Link prodotti per kit</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(KIT_NAMES).map(([kitId, kitName]) => (
                  <div key={kitId} className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-500 w-24 flex-shrink-0">{kitName}</span>
                    <Input
                      value={shopForm.product_links[kitId] || ''}
                      onChange={e => updateShopLink(kitId, e.target.value)}
                      placeholder="https://..."
                      className="h-8 text-xs"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={shopForm.active} onChange={e => setShopForm(p => ({ ...p, active: e.target.checked }))} className="rounded" /> Attivo</label>
              <div className="flex-1" />
              <Button variant="outline" size="sm" onClick={resetForm}><X className="w-3 h-3 mr-1" /> Annulla</Button>
              <Button data-testid="save-shop-btn" size="sm" onClick={handleSaveShop} className="bg-stocker-green text-white"><Save className="w-3 h-3 mr-1" /> Salva</Button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input data-testid="search-input" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={tab === 'physical' ? 'Cerca nome, città, CAP, provincia…' : 'Cerca…'} className="pl-9 h-9" />
          </div>
          <select data-testid="filter-active" value={filterActive} onChange={e => setFilterActive(e.target.value)} className="h-9 border rounded-md px-3 text-sm">
            <option value="all">Tutti</option><option value="active">Attivi</option><option value="inactive">Inattivi</option>
          </select>
          <span className="text-xs text-gray-500">{tab === 'physical' ? filteredDealers.length : filteredShops.length} risultati</span>
        </div>

        {/* ======== PHYSICAL DEALERS TABLE ======== */}
        {tab === 'physical' && (
          loading ? <p className="text-center py-12 text-gray-500">Caricamento...</p> : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b">
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Stato</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Nome</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Indirizzo</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Città</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">CAP</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Prov.</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Telefono</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Coord.</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Azioni</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredDealers.map(d => (
                      <tr key={d.dealer_id} className={`hover:bg-gray-50 ${!d.active ? 'opacity-50' : ''}`}>
                        <td className="px-3 py-2"><button onClick={() => handleToggleDealer(d)}>{d.active ? <ToggleRight className="w-6 h-6 text-stocker-green" /> : <ToggleLeft className="w-6 h-6 text-gray-400" />}</button></td>
                        <td className="px-3 py-2 font-medium text-gray-900 max-w-[180px]"><div className="truncate">{d.name}</div>{d.website && <a href={d.website} target="_blank" rel="noopener noreferrer" className="text-[10px] text-stocker-green hover:underline flex items-center gap-0.5"><Globe className="w-3 h-3" /> Web</a>}</td>
                        <td className="px-3 py-2 text-gray-600 max-w-[160px] truncate">{d.address || '-'}</td>
                        <td className="px-3 py-2 text-gray-600 max-w-[120px] truncate">{d.city || '-'}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{d.postcode || '-'}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs uppercase">{d.provincia || d.regions?.[0] || '-'}</td>
                        <td className="px-3 py-2 text-gray-600">{d.phone ? <a href={`tel:${d.phone}`} className="flex items-center gap-1 hover:text-stocker-green"><Phone className="w-3 h-3" /> {d.phone}</a> : '-'}</td>
                        <td className="px-3 py-2 text-gray-600 max-w-[140px] truncate">{d.email ? <a href={`mailto:${d.email}`} className="hover:text-stocker-green">{d.email}</a> : '-'}</td>
                        <td className="px-3 py-2 text-xs">{d.lat && d.lng ? <span className="text-green-600">{d.lat.toFixed(2)}, {d.lng.toFixed(2)}</span> : <span className="text-red-400">Mancante</span>}</td>
                        <td className="px-3 py-2"><div className="flex gap-1">
                          <button onClick={() => handleEditDealer(d)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => handleDeleteDealer(d.dealer_id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}

        {/* ======== ONLINE SHOPS TABLE ======== */}
        {tab === 'online' && (
          loading ? <p className="text-center py-12 text-gray-500">Caricamento...</p> : (
            <div className="space-y-3">
              {filteredShops.map(s => (
                <div key={s.shop_id} data-testid={`shop-row-${s.shop_id}`} className={`bg-white rounded-xl border border-gray-200 p-4 ${!s.active ? 'opacity-50' : ''}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <button onClick={() => handleToggleShop(s)}>{s.active ? <ToggleRight className="w-6 h-6 text-stocker-green" /> : <ToggleLeft className="w-6 h-6 text-gray-400" />}</button>
                    <div className="w-10 h-10 bg-gray-50 rounded-lg border flex items-center justify-center overflow-hidden">
                      {s.logo_url ? <img src={s.logo_url} alt={s.name} className="w-8 h-8 object-contain" /> : <ShoppingCart className="w-5 h-5 text-gray-400" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-sm">{s.name}</p>
                      <p className="text-[11px] text-gray-400">Posizione: {s.position} · {Object.values(s.product_links || {}).filter(Boolean).length} link attivi</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => handleEditShop(s)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDeleteShop(s.shop_id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  {/* Product links */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                    {Object.entries(KIT_NAMES).map(([kitId, kitName]) => {
                      const link = s.product_links?.[kitId];
                      return (
                        <div key={kitId} className={`flex items-center gap-1.5 px-2 py-1 rounded text-[11px] ${link ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'}`}>
                          <Link2 className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{kitName}</span>
                          {link ? <span className="ml-auto text-green-500 font-bold text-[10px]">OK</span> : <span className="ml-auto">-</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {filteredShops.length === 0 && <p className="text-center py-12 text-gray-400">Nessun online shop</p>}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AdminDealers;
